/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Component: MovieSearch
 * File     : MovieSearch.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   Reusable React UI component: MovieSearch, composed within pages and other components.
 *   Exports: MovieSearch
 *   
 *
 * Dependencies:
 *   - ../../services/api
 *   - @mui/icons-material
 *   - @mui/material/Avatar
 *   - date-fns
 *   - lodash
 *   - react
 *
 * Usage:
 *   Import: import MovieSearch from '@/components/polls/MovieSearch';
 * -----------------------------------------------------------------------------
 */
import React, { useState, useCallback } from 'react';
import {
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemAvatar,
  Paper,
  CircularProgress,
  Typography,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  OutlinedInput,
  Checkbox,
  ListItemText as MuiListItemText,
} from '@mui/material';
import { Search as SearchIcon, Movie as MovieIcon } from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { debounce } from 'lodash';
import { searchOMDB, plexSearch, searchPhysicalMedia, buildApiUrl } from '../../services/api';
import Avatar from '@mui/material/Avatar';

const MovieSearch = ({ onMovieSelect, selectedMovies = [] }) => {
  const extractImdbFromGuid = (guid) => {
    if (!guid || typeof guid !== 'string') return null;
    const m = guid.match(/(tt\d{3,8})/i);
    return m ? m[1] : null;
  };
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchSources, setSearchSources] = useState(['omdb']); // Array of selected sources

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(async (searchQuery) => {
      if (!searchQuery.trim()) {
        setResults([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        
        const searchPromises = [];
        const results = [];
        
        // Search based on selected sources
        if (searchSources.includes('plex')) {
          searchPromises.push(
            plexSearch(searchQuery).catch(() => ({ results: [] }))
          );
        }
        
        if (searchSources.includes('omdb')) {
          searchPromises.push(
            searchOMDB(searchQuery).catch(() => ({ Search: [] }))
          );
        }
        
        if (searchSources.includes('personal')) {
          searchPromises.push(
            searchPhysicalMedia(searchQuery).catch((error) => {
              console.error('Personal media search error:', error);
              return { items: [] };
            })
          );
        }
        
        const searchResults = await Promise.all(searchPromises);
        
        // Process results based on selected sources
        let resultIndex = 0;
        
        // Process Plex results
        if (searchSources.includes('plex')) {
          const plexResult = searchResults[resultIndex++];
          const plexMapped = (plexResult.results || []).filter(p => p && p.title).map(p => ({
            imdbID: extractImdbFromGuid(p.guid) || undefined,
            Title: p.title,
            Year: p.year || '',
            Poster: p.thumb || '',
            Type: 'movie',
            _onPlex: true,
            _plexUrl: p.url,
            _plexGuid: p.guid,
          }));
          results.push(...plexMapped);
        }
        
        // Process OMDB results
        if (searchSources.includes('omdb')) {
          const omdbResult = searchResults[resultIndex++];
          const omdb = omdbResult.Search || [];
          results.push(...omdb);
        }
        
        // Process Personal Media results
        if (searchSources.includes('personal')) {
          const personalResult = searchResults[resultIndex++];
          const personal = personalResult?.items || [];
          console.log('Personal media results:', personal);
          results.push(...personal);
        }
        
        // Remove duplicates (by title+year) when searching multiple sources
        if (searchSources.length > 1) {
          const seen = new Set();
          const uniqueResults = results.filter(item => {
            const key = `${item.Title}|${item.Year}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          console.log('Final results (multiple sources):', uniqueResults);
          setResults(uniqueResults);
        } else {
          console.log('Final results (single source):', results);
          setResults(results);
        }
        
      } catch (err) {
        setError('Failed to search movies. Please try again.');
        console.error('Search error:', err);
      } finally {
        setLoading(false);
      }
    }, 500),
    [searchSources]
  );

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    debouncedSearch(value);
  };

  const handleSourceChange = (event) => {
    const value = event.target.value;
    setSearchSources(typeof value === 'string' ? value.split(',') : value);
  };

  const handleSelectMovie = (movie) => {
    onMovieSelect(movie);
    setQuery('');
    setResults([]);
  };

  const normalize = (s) => (s || '').toString().trim().toLowerCase();
  const uniqueKey = (m) => (m?.imdbID && m.imdbID !== 'N/A') ? m.imdbID : `${normalize(m?.Title)}|${normalize(m?.Year)}`;
  const isMovieSelected = (movie) => selectedMovies.some(sel => uniqueKey(sel) === uniqueKey(movie));

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search for movies..."
            value={query}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Search Sources</InputLabel>
            <Select
              multiple
              value={searchSources}
              onChange={handleSourceChange}
              input={<OutlinedInput label="Search Sources" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value === 'omdb' ? 'OMDB' : value === 'plex' ? 'Plex' : value === 'personal' ? 'Personal Media' : value} size="small" />
                  ))}
                </Box>
              )}
            >
              <MenuItem value="omdb">
                <Checkbox checked={searchSources.indexOf('omdb') > -1} />
                <MuiListItemText primary="OMDB" />
              </MenuItem>
              <MenuItem value="plex">
                <Checkbox checked={searchSources.indexOf('plex') > -1} />
                <MuiListItemText primary="Plex" />
              </MenuItem>
              <MenuItem value="personal">
                <Checkbox checked={searchSources.indexOf('personal') > -1} />
                <MuiListItemText primary="Personal Media" />
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {loading && (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {results.length > 0 && (
        <Paper elevation={3} sx={{ maxHeight: 300, overflow: 'auto' }}>
          <List>
            {results.filter(movie => movie && movie.Title).map((movie) => (
              <ListItem key={uniqueKey(movie)} disablePadding>
                <ListItemButton
                  onClick={() => handleSelectMovie(movie)}
                  disabled={isMovieSelected(movie)}
                >
                  <ListItemAvatar>
                    <Avatar
                      variant="rounded"
                      src={movie.Poster && movie.Poster !== 'N/A' ? (movie._onPlex ? buildApiUrl(`/api/plex/image?path=${encodeURIComponent(movie.Poster)}`) : movie.Poster) : undefined}
                      alt={`${movie.Title} poster`}
                      sx={{ width: 40, height: 56, mr: 2 }}
                    >
                      <MovieIcon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={movie.Title}
                    secondary={
                      movie.lastWonAt
                        ? `${movie.Year} - ${movie.Type} • previously won ${formatDistanceToNow(new Date(movie.lastWonAt))} ago`
                        : `${movie.Year} - ${movie.Type}`
                    }
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {movie._onPlex && (
                      <Chip label="On Plex" size="small" color="success" component="a" clickable href={movie._plexUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} />
                    )}
                    {movie._isPersonalMedia && (
                      <Chip label="Personal Media" size="small" color="primary" />
                    )}
                    {isMovieSelected(movie) && (
                    <Chip label="Added" size="small" color="success" />
                    )}
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {selectedMovies.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Movies:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
              {selectedMovies.filter(movie => movie && movie.Title).map((movie) => (
              <Chip
                key={uniqueKey(movie)}
                avatar={
                  <Avatar
                      src={movie.Poster && movie.Poster !== 'N/A' ? (movie._onPlex ? buildApiUrl(`/api/plex/image?path=${encodeURIComponent(movie.Poster)}`) : movie.Poster) : undefined}
                    alt={`${movie.Title} poster`}
                  >
                    <MovieIcon fontSize="small" />
                  </Avatar>
                }
                label={`${movie.Title} (${movie.Year})`}
                onDelete={() => onMovieSelect(movie, true)}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MovieSearch;
